import { LayoutSectionComponent } from '@/components/LayoutSection';
import {
  Button,
  Checkbox,
  FormContainer,
  FormItem,
  NativeSelect,
  Row,
  Select,
  TextArea,
} from 'elmo-elements';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import React, { ReactElement, useEffect } from 'react';
import {
  useGetCurrentTenantProfileQuery,
  useUpdateTenantProfileMutation,
} from '@/modules/services';
import { TenantProfileType } from '@/modules/services/auth';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useDispatch } from 'react-redux';
import { useUserHasRole } from '@/lib/auth/useUserHasRoles';
import { Role } from '@/lib/auth/roles/role.enum';
import MyLibraryRoutes from '@/pages/MyLibrary/MyLibraryRoutes';
import { generatePath, useHistory } from 'react-router-dom';

interface TenantProfileSchemaType {
  tenant: {
    subdomain: string;
  };
  configMetadata: {
    mobileContentEnable: number;
    allowCloseQuiz: number;
    courseTrackingEnable: number;
    regions: string[];
    enableCourseLibrary: number;
    enableVideoLibrary: number;
    enableBreathe: number;
  };
  plan: number;
  scorm: number;
  elearningBookmarkEnable: number;
}

const learningManagementSystemOptions = ['LCMS', 'LMS', 'LCMS+LMS', 'Breathe'];
const regionsArray = ['AU', 'NZ', 'SG', 'MY', 'GB', 'CA', 'CAF'];
const regionOptions = regionsArray.map((region) => ({
  value: region,
  label: region,
}));

const schema = yup.object().shape({
  tenant: yup.object().shape({
    subdomain: yup
      .string()
      .max(255, 'Subdomain must be at most 255 characters'),
  }),
  configMetadata: yup.object().shape({
    mobileContentEnable: yup.number(),
    allowCloseQuiz: yup.number(),
    courseTrackingEnable: yup.number(),
    enableBreathe: yup.number(),
    regions: yup
      .array()
      .of(yup.string().max(10))
      .min(1, 'Must have selected at least 1 region')
      .max(10, 'Maximum of 10 regions selected'),
  }),
  plan: yup.number(),
  scorm: yup.number().min(0).max(1),
  elearningBookmarkEnable: yup.number(),
});

const defaultValue = {
  tenant: {
    subdomain: '',
  },
  configMetadata: {
    mobileContentEnable: 0,
    allowCloseQuiz: 0,
    courseTrackingEnable: 0,
    enableCourseLibrary: 0,
    enableVideoLibrary: 0,
    regions: [regionsArray[0]],
  },
  plan: 0,
  scorm: 0,
  elearningBookmarkEnable: 0,
};

const PublishingComponent = (): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const hasAdminRole = useUserHasRole([Role.Admin, Role.Super, Role.Migration]);
  const tenant = useGetCurrentTenantProfileQuery();
  const [updateTenantProfile, updateTenantProfileStatus] =
    useUpdateTenantProfileMutation();

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TenantProfileSchemaType>({
    resolver: yupResolver(schema),
    defaultValues: defaultValue,
  });

  useEffect(() => {
    if (!tenant.isLoading && tenant.data) {
      const tenantData = {
        ...tenant.data,
        tenant: {
          ...tenant.data.tenant,
          subdomain: tenant.data.tenant.subdomain ?? '',
        },
        configMetadata: {
          ...defaultValue.configMetadata,
          ...tenant.data.configMetadata,
          regions: tenant.data.configMetadata?.regions ?? [],
        },
      };
      // Prepopulate form with tenant-profile data
      reset(tenantData);
    }
  }, [tenant.data, tenant.isLoading, reset]);

  useEffect(() => {
    if (
      !updateTenantProfileStatus.isError &&
      !updateTenantProfileStatus.isSuccess
    )
      return;

    let message = 'Update tenant profile error! Something went wrong.';
    if (updateTenantProfileStatus.isSuccess) {
      message = 'Update tenant profile successful.';
    }

    dispatch({
      type: flashMessageActions.addFlashMessage,
      payload: {
        isActive: true,
        message,
      },
    });
  }, [updateTenantProfileStatus]);

  const regionsData = watch('configMetadata.regions');

  if (hasAdminRole !== undefined && !hasAdminRole) {
    history.replace(generatePath(MyLibraryRoutes.path));
  }

  return (
    <form
      onSubmit={handleSubmit((submitData) => {
        if (!tenant.data) return;

        const hasRegionSelected =
          (submitData.configMetadata.regions?.length ?? 0) > 0;
        const tenantRegions = [...(tenant.data.configMetadata?.regions ?? [])];
        const regions = hasRegionSelected
          ? submitData.configMetadata.regions
          : tenantRegions;

        const tenantData = {
          ...tenant.data,
          ...submitData,
          tenant: {
            ...submitData.tenant,
            // Regex to replace double line breaks with single line break
            subdomain: (submitData.tenant.subdomain ?? '').replace(
              /\n{2,}/g,
              '\n',
            ),
          },
          configMetadata: {
            ...tenant.data.configMetadata,
            ...submitData.configMetadata,
            regions,
          },
        };

        updateTenantProfile(tenantData as TenantProfileType);
      })}
    >
      <LayoutSectionComponent
        className="remove-overflow-hidden"
        heading="Setup"
        isFirst={true}
        content={
          <FormContainer>
            <FormItem
              label="Learning Management System"
              message={errors.plan && errors.plan?.message}
              status={errors.plan ? 'error' : 'default'}
              isBlock={true}
              size="sm"
            >
              <NativeSelect
                value={watch('plan')}
                onChange={(e) => {
                  setValue('plan', +e);
                }}
              >
                {learningManagementSystemOptions.map(
                  (plan: string, index: number) => (
                    <NativeSelect.Option
                      label={plan}
                      value={index}
                      key={index}
                    />
                  ),
                )}
              </NativeSelect>
            </FormItem>
            <FormItem
              label="Scorm enabled"
              message={errors.scorm && errors.scorm.message}
              status={errors.scorm ? 'error' : 'default'}
              isBlock={true}
              size="sm"
            >
              <NativeSelect
                value={watch('scorm')}
                {...register('scorm', {})}
                onChange={(e) => setValue('scorm', +e)}
                ariaLabel="scorm enabled"
                id="scorm"
              >
                <NativeSelect.Option label="No" value={0} />
                <NativeSelect.Option label="Yes" value={1} />
              </NativeSelect>
            </FormItem>
            <FormItem
              label="Video Content Region"
              message={
                errors.configMetadata?.regions &&
                errors.configMetadata?.regions?.message
              }
              status={errors.configMetadata?.regions ? 'error' : 'default'}
              isBlock={true}
            >
              <Select
                onChange={(e: { value: string; label: string }[]) => {
                  const regionsSelected = e.map(
                    (regionOption) => regionOption.value,
                  );
                  setValue('configMetadata.regions', regionsSelected);
                }}
                value={regionsData.map((region) => ({
                  value: region,
                  label: region,
                }))}
                isMulti
                options={regionOptions}
              />
            </FormItem>
            <FormItem
              label="Subdomain"
              message={
                errors.tenant?.subdomain && errors.tenant.subdomain?.message
              }
              status={errors.tenant?.subdomain ? 'error' : 'default'}
              isBlock={true}
            >
              <TextArea
                ariaLabel="subdomain"
                {...register('tenant.subdomain', {})}
                name="tenant.subdomain"
                onChange={(e) => {
                  setValue(
                    'tenant.subdomain',
                    (e.target as HTMLTextAreaElement).value,
                  );
                }}
                value={watch('tenant.subdomain')}
              />
            </FormItem>
          </FormContainer>
        }
      />
      <LayoutSectionComponent
        className="remove-overflow-hidden"
        heading="Preferences"
        content={
          <FormContainer className="mt-5">
            <FormItem
              className="m-0"
              message={
                errors.configMetadata?.allowCloseQuiz &&
                errors.configMetadata?.allowCloseQuiz?.message
              }
              status={
                errors.configMetadata?.allowCloseQuiz ? 'error' : 'default'
              }
            >
              <Checkbox
                onChange={(e: any) => {
                  setValue(
                    'configMetadata.allowCloseQuiz',
                    e.target.checked ? 1 : 0,
                  );
                }}
                isChecked={watch('configMetadata.allowCloseQuiz') > 0}
                name="configMetadata.allowCloseQuiz"
                id="configMetadata.allowCloseQuiz"
                label="Close quiz"
                ariaLabel="close quiz checkbox"
              />
            </FormItem>
            <FormItem
              className="m-0"
              message={
                errors.configMetadata?.courseTrackingEnable &&
                errors.configMetadata?.courseTrackingEnable?.message
              }
              status={
                errors.configMetadata?.courseTrackingEnable
                  ? 'error'
                  : 'default'
              }
            >
              <Checkbox
                onChange={(e: any) => {
                  setValue(
                    'configMetadata.courseTrackingEnable',
                    e.target.checked ? 1 : 0,
                  );
                }}
                isChecked={watch('configMetadata.courseTrackingEnable') > 0}
                name="configMetadata.courseTrackingEnable"
                id="configMetadata.courseTrackingEnable"
                label="Course tracking"
                ariaLabel="course tracking checkbox"
              />
            </FormItem>
            <FormItem
              className="m-0"
              message={
                errors.elearningBookmarkEnable &&
                errors.elearningBookmarkEnable?.message
              }
              status={
                errors.configMetadata?.allowCloseQuiz ? 'error' : 'default'
              }
            >
              <Checkbox
                onChange={(e: any) => {
                  setValue('elearningBookmarkEnable', e.target.checked ? 1 : 0);
                }}
                isChecked={watch('elearningBookmarkEnable') > 0}
                name="elearningBookmarkEnable"
                id="elearningBookmarkEnable"
                label="Course bookmark"
                ariaLabel="course bookmark checkbox"
              />
            </FormItem>
            <FormItem
              className="m-0"
              message={
                errors.configMetadata?.mobileContentEnable &&
                errors.configMetadata?.mobileContentEnable?.message
              }
              status={
                errors.configMetadata?.mobileContentEnable ? 'error' : 'default'
              }
            >
              <Checkbox
                onChange={(e: any) => {
                  setValue(
                    'configMetadata.mobileContentEnable',
                    e.target.checked ? 1 : 0,
                  );
                }}
                isChecked={watch('configMetadata.mobileContentEnable') > 0}
                name="configMetadata.mobileContentEnable"
                id="configMetadata.mobileContentEnable"
                label="Mobile access"
                ariaLabel="mobile access checkbox"
              />
            </FormItem>
            <FormItem
              className="m-0"
              message={
                errors.configMetadata?.enableCourseLibrary &&
                errors.configMetadata?.enableCourseLibrary?.message
              }
              status={
                errors.configMetadata?.enableCourseLibrary ? 'error' : 'default'
              }
            >
              <Checkbox
                onChange={(e: any) => {
                  // TODO: (Om) Have a const each main entity from where we will reference this. Like the entity in api.
                  setValue(
                    'configMetadata.enableCourseLibrary',
                    e.target.checked ? 1 : 0,
                  );
                }}
                isChecked={watch('configMetadata.enableCourseLibrary') > 0}
                name="configMetadata.enableCourseLibrary"
                id="configMetadata.enableCourseLibrary"
                label="Course Library"
                ariaLabel="course library checkbox"
              />
            </FormItem>
            <FormItem
              className="m-0"
              message={
                errors.configMetadata?.enableVideoLibrary &&
                errors.configMetadata?.enableVideoLibrary?.message
              }
              status={
                errors.configMetadata?.enableVideoLibrary ? 'error' : 'default'
              }
            >
              <Checkbox
                onChange={(e: any) => {
                  setValue(
                    'configMetadata.enableVideoLibrary',
                    e.target.checked ? 1 : 0,
                  );
                }}
                isChecked={watch('configMetadata.enableVideoLibrary') > 0}
                name="configMetadata.enableVideoLibrary"
                id="configMetadata.enableVideoLibrary"
                label="Video Library"
                ariaLabel="video library checkbox"
              />
            </FormItem>
          </FormContainer>
        }
      />

      <Row className="mt-5">
        <Button
          type="primary"
          htmlType="submit"
          ariaLabel="Submit Form"
          isUppercase={false}
          className="px-5"
        >
          Save
        </Button>
      </Row>
    </form>
  );
};

export default PublishingComponent;
